import {auditInAnimationFrame} from '@frontapp/editor-html';
import _, {isEqual} from 'lodash';
import {DateTime} from 'luxon';
import {EMPTY, from, fromEvent, merge, Observable, OperatorFunction, Subscription} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  mergeMap,
  share,
  switchMap,
} from 'rxjs/operators';

import {buildWindow} from '../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {getWindow} from '../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {isDefined} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {tooltipDefaultDelay} from '../../../../libs/shared/ui/src/components/tooltips/tooltipHelpers';
import {computeHoverPlaceholderHeight} from './fullCalendarHelpers';

/*
 * Api.
 */

/** Creates an observable that emits info about the hovered cell given an observable for the wrapper element. */
export function makeHoverInfo(
  wrapper$: Observable<HTMLElement | null>,
  placeholderDuration: () => number,
): Observable<HoverPlaceholderData | undefined> {
  return wrapper$.pipe(
    map((wrapper) => ({
      wrapper,
      nodes: wrapper ? Array.from(wrapper.querySelectorAll<HTMLElement>('.fc-slats tr[data-time]')) : [],
    })),
    switchMap((data) => makeMouseHoverObservable(data)),
    map((data) => findDateElementAndDimensions(data, placeholderDuration)),
    distinctUntilChanged(isEqual),
    share(),
  );
}

/** Creates a subscription that updates the placeholder and tooltip from the placeholder info observable. */
export function buildHoverSubsciption(
  hoverInfo$: Observable<HoverPlaceholderData | undefined>,
  updatePlaceholder: (arg: HoverPlaceholderData | undefined) => void,
  updatePlaceholderTooltip: (arg: boolean) => void,
) {
  const subscription = new Subscription();
  subscription.add(hoverInfo$.subscribe((info) => updatePlaceholder(info)));
  subscription.add(
    hoverInfo$
      .pipe(computeTooltipVisibility())
      .subscribe((isTooltipVisible) => updatePlaceholderTooltip(isTooltipVisible)),
  );
  return subscription;
}

/*
 * Helpers.
 */

interface TimeRowData {
  nodes: ReadonlyArray<HTMLElement>;
  wrapper: HTMLElement | null;
}

interface MouseHoverData {
  node: HTMLElement;
  mouseX: number;
  wrapper: HTMLElement;
}

interface HoverPlaceholderData {
  dateElement: HTMLElement;
  y: number;
  height: number;
}

/** Creates an observable that emits the HTMLElement and mouseX when hovering over the horizontal time rows passed in. */
function makeMouseHoverObservable(data: TimeRowData): Observable<MouseHoverData | undefined> {
  const {nodes, wrapper} = data;
  if (!wrapper) {
    return EMPTY;
  }

  const window = getWindow(wrapper);
  if (!window) {
    return EMPTY;
  }

  return from(nodes).pipe(
    // Map each node to an observable of mouseX values.
    mergeMap((node) =>
      merge(
        fromEvent<MouseEvent>(node, 'mousemove').pipe(
          map((event) => ({node, mouseX: event.clientX, wrapper})),
        ),
        fromEvent<MouseEvent>(node, 'mouseleave').pipe(mapTo(undefined)),
      ),
    ),
    // Emit the most recent hover info in the next animation frame.
    auditInAnimationFrame(buildWindow(window)),
  );
}

/** Creates an observable that emits the vertical size and date element for a placeholder given mouse data. */
function findDateElementAndDimensions(
  data: MouseHoverData | undefined,
  placeholderDuration: () => number,
): HoverPlaceholderData | undefined {
  if (!data) {
    return undefined;
  }

  const {node: timeElement, mouseX, wrapper} = data;
  const timeRect = timeElement.getBoundingClientRect();
  const time = timeElement.getAttribute('data-time');

  // Find the correct date column for the hover state.
  return _(wrapper.querySelectorAll<HTMLElement>('.fc-time-grid .fc-bg td[data-date]:not(.fc-past)'))
    .map((dateElement) => {
      const dateRect = dateElement.getBoundingClientRect();

      // Get the date element in the same column as the cursor.
      const isHoveringDateElement = mouseX >= dateRect.x && mouseX < dateRect.x + dateRect.width;
      if (!isHoveringDateElement) {
        return undefined;
      }

      const date = dateElement.getAttribute('data-date');
      const hoverStartDateTime = DateTime.fromFormat(`${date} ${time}`, 'yyyy-MM-dd HH:mm:ss');
      const hoverDurationMinutes = placeholderDuration;

      // Don't allow hovering if an event created at this slot would end before now.
      if (hoverStartDateTime.plus({minutes: hoverDurationMinutes()}) < DateTime.local()) {
        return undefined;
      }

      return {
        dateElement,
        y: timeRect.y - dateRect.y,
        height: computeHoverPlaceholderHeight(timeElement, hoverDurationMinutes()),
      };
    })
    .compact()
    .head();
}

function computeTooltipVisibility(): OperatorFunction<HoverPlaceholderData | undefined, boolean> {
  return (hoverInfo$) =>
    merge(
      // Hide the tooltip every time the hovered cell changes.
      hoverInfo$.pipe(mapTo(false)),
      // Show the tooltip if the hovered cell stays the same for a little while.
      hoverInfo$.pipe(
        // Wait for the hovered cell (or the absence of a hovered cell) to stay the same.
        debounceTime(tooltipDefaultDelay),
        // Show the tooltip only when a cell is being hovered.
        filter(isDefined),
        mapTo(true),
      ),
    ).pipe(distinctUntilChanged());
}
